import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { functionsApi } from "@/api/functions";
import { runsApi } from "@/api/runs";
import { Card } from "@/components/ui/card";
import { StatCard } from "@/components/stat-card";
import { StatusBadge } from "@/components/status-badge";
import { JsonViewer } from "@/components/json-viewer";
import { EmptyState } from "@/components/empty-state";
import { ScheduleCard } from "@/components/schedule-card";
import { DetailSkeleton } from "@/components/detail-skeleton";
import { TriggerBadge } from "./_authed.functions.index";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRelative, formatDurationMs } from "@/lib/format";
import { POLL, isRunActive } from "@/lib/poll";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export const Route = createFileRoute("/_authed/functions/$id")({
  component: FunctionDetailPage,
});

function FunctionDetailPage() {
  const { id } = Route.useParams();

  const fnQ = useQuery({
    queryKey: ["function", id],
    queryFn: () => functionsApi.get(id),
  });
  const metricsQ = useQuery({
    queryKey: ["function-metrics", id],
    queryFn: () => functionsApi.metrics(id, "24h"),
    refetchInterval: POLL.config,
  });
  const runsQ = useQuery({
    queryKey: ["function-runs", id],
    queryFn: () => runsApi.list({ function_id: id, limit: 50 }),
    refetchInterval: (query) => {
      if (!query.state.data) return POLL.active;
      const hasActive = query.state.data.data.some((r) => isRunActive(r.status));
      return hasActive ? POLL.active : POLL.idle;
    },
  });

  if (fnQ.isLoading) return <DetailSkeleton />;
  if (fnQ.isError || !fnQ.data) {
    return <div className="text-fail">Function not found</div>;
  }

  const fn = fnQ.data;
  const m = metricsQ.data;
  const chartData = (m?.buckets ?? []).map((b) => ({
    label: new Date(b.hour_start).toISOString().slice(11, 16),
    total: b.total,
    failed: b.failed,
  }));
  const recent = runsQ.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/functions" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft size={13} /> Functions
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl font-semibold tracking-tight font-mono">{fn.slug}</h1>
          <TriggerBadge type={fn.trigger_type} />
        </div>
        <div className="mt-1 font-mono text-xs text-muted-foreground">
          {fn.app_name} · {fn.trigger_event ?? fn.trigger_cron ?? "—"}
        </div>
      </div>

      <ScheduleCard fn={fn} />

      {m && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard label="Total (24h)" value={m.total} accent="brand" />
          <StatCard label="Completed" value={m.completed} accent="ok" />
          <StatCard label="Failed" value={m.failed} accent="fail" />
          <StatCard label="P95 duration" value={formatDurationMs(m.p95_ms)} accent="idle" />
        </div>
      )}

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-[15px] font-semibold tracking-tight">Throughput</h2>
            <p className="text-xs text-muted-foreground">Runs per hour · last 24h</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-brand" /> total</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-fail" /> failed</span>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }} barGap={2}>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.5} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} interval={3}
                tick={{ fontSize: 11, fontFamily: "var(--font-mono)", fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={40}
                tick={{ fontSize: 11, fontFamily: "var(--font-mono)", fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip
                contentStyle={{
                  fontSize: 12, fontFamily: "var(--font-mono)",
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8, boxShadow: "0 8px 24px rgb(0 0 0 / 0.35)",
                  color: "hsl(var(--popover-foreground))",
                }}
                labelStyle={{ color: "hsl(var(--muted-foreground))", marginBottom: 4 }}
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
              />
              <Bar dataKey="total" fill="var(--brand)" radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="failed" fill="var(--st-fail)" radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Config</h2>
        </div>
        <div className="p-5"><JsonViewer data={fn.config} /></div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Recent runs</h2>
        </div>
        {recent.length === 0 ? (
          <EmptyState
            title="No runs yet"
            description="Trigger this function via its event to see runs here."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Run</TableHead>
                <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Attempt</TableHead>
                <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((r) => (
                <TableRow key={r.id} className="group cursor-pointer">
                  <TableCell className="pl-5"><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <Link to="/runs/$id" params={{ id: r.id }}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                      {r.id.slice(0, 8)}
                    </Link>
                  </TableCell>
                  <TableCell className="tabular text-right font-mono text-[13px] text-muted-foreground">{r.attempt}</TableCell>
                  <TableCell className="tabular pr-5 text-right font-mono text-[13px] text-muted-foreground">
                    {formatRelative(r.started_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
